import BarGraphIcon from "../../../Assets/Img/BarGraphIcon";
import CostBenchIcon from "../../../Assets/Img/CostBenchIcon";
import FIBenchmarkIcon from "../../../Assets/Img/FIBenchmarkIcon";
import InspectionIcon from "../../../Assets/Img/InspectionIcon";
import ContractorPerformanceIcon from "../../../Assets/Img/ContractorPerformanceIcon";
import LienWaiverIcon from "../../../Assets/Img/LienWaiverIcon";
import PlatformUsageIcon from "../../../Assets/Img/PlatformUsageIcon";
import { chartPalette } from "../data/chartData";
import type { ReportSection, Crumb } from "../types/reports.types";

export const reportTopCrumbs: Crumb[] = [
  { label: "Dashboard", link: "/admin" },
  { label: "Reports" },
];

export const reportSections: ReportSection[] = [
  {
    id: "projectMix",
    navLabel: "Project Mix",
    navIcon: <BarGraphIcon />,
    navGroup: "Portfolio",
    title: "Project Mix & Breakdown",
    subtitle: "Portfolio composition by type, lender, value range, and geography",
    filters: ["1Y", "2Y", "5Y", "All"],
    defaultFilter: "2Y",
    metrics: [
      { label: "Total Projects",      value: "47",    delta: "up",      note: "12 vs prior period"   },
      { label: "Total Contract Value", value: "$68.4M", delta: "up",     note: "18% vs prior period"  },
      { label: "Avg Contract Value",   value: "$1.45M", delta: "neutral", note: "stable"               },
      { label: "Active Banks",         value: "9",     delta: "up",      note: "2 new lenders"        },
    ],
    rows: [
      [
        {
          title: "Residential vs. Commercial",
          subtitle: "Project count by type",
          chart: {
            kind: "pie",
            dataKey: "projectMixShare",
            colors: [chartPalette.blue, chartPalette.blueLight],
          },
          actions: [{ label: "Export", toast: "Residential vs Commercial exported" }],
        },
        {
          title: "New Projects by Month",
          subtitle: "All project types",
          chart: {
            kind: "bar",
            dataKey: "monthlyNewProjects",
            bars: [{ dataKey: "value", name: "Projects", color: chartPalette.blue }],
          },
        },
      ],
      [
        {
          title: "Projects by Lender / Bank",
          subtitle: "Project count per institution",
          chart: {
            kind: "bar",
            dataKey: "projectsByBank",
            layout: "vertical",
            bars: [{ dataKey: "value", name: "Projects", color: chartPalette.blue }],
          },
        },
        {
          title: "Contract Value Distribution",
          subtitle: "Projects by value range",
          chart: {
            kind: "bar",
            dataKey: "contractValueDistribution",
            layout: "vertical",
            bars: [{ dataKey: "value", name: "Projects", color: chartPalette.blueLight }],
          },
          insight: {
            tone: "info",
            title: "Insight",
            text: "The $500K-$1.5M residential bracket accounts for 45% of all projects and drives 38% of CSC inspection revenue.",
          },
        },
      ],
    ],
  },
  {
    id: "drawActivity",
    navLabel: "Draw & Inspection",
    navIcon: <InspectionIcon />,
    navGroup: "Portfolio",
    title: "Draw & Inspection Activity",
    subtitle: "Draw volume, inspection turnaround, and CSC fee revenue over time",
    filters: ["3M", "1Y", "2Y"],
    defaultFilter: "1Y",
    metrics: [
      { label: "Draws Processed", value: "184",   delta: "up",   note: "24% YoY"          },
      { label: "Avg Turnaround",  value: "4.2d",  delta: "up",   note: "0.8d faster YoY"  },
      { label: "Flagged Draws",   value: "31",    delta: "warn", note: "16.8% flag rate"   },
      { label: "CSC Fee Revenue", value: "$94.2K", delta: "up",   note: "31% YoY"           },
    ],
    rows: [
      [
        {
          title: "Monthly Draw Volume & Flag Rate",
          subtitle: "Trends for draws and flagged percentage",
          wide: true,
          chart: {
            kind: "line",
            dataKey: "drawVolumeAndFlags",
            lines: [
              { dataKey: "draws",    name: "Draws",       color: chartPalette.blue  },
              { dataKey: "flagRate", name: "Flag Rate %", color: chartPalette.amber },
            ],
          },
        },
      ],
      [
        {
          title: "Inspection Turnaround",
          subtitle: "Avg days from draw submission to CSC report delivery",
          chart: {
            kind: "line",
            dataKey: "inspectionTurnaround",
            lines: [{ dataKey: "value", name: "Days", color: chartPalette.green }],
          },
          insight: {
            tone: "success",
            title: "Trending Positive",
            text: "Average turnaround improved from 5.0d to 4.2d over the past 12 months.",
          },
        },
        {
          title: "CSC Fee Revenue by Month",
          subtitle: "Pre-con reports + inspection fees",
          chart: {
            kind: "area",
            dataKey: "cscFeeRevenue",
            lines: [
              { dataKey: "value", name: "Revenue", color: chartPalette.blue, fill: chartPalette.blueSoft },
            ],
          },
        },
      ],
    ],
  },
  {
    id: "fiBenchmark",
    navLabel: "FI % Benchmarks",
    navIcon: <FIBenchmarkIcon />,
    navGroup: "Intelligence",
    title: "FI Phase % Value Benchmarks",
    subtitle: "Observed averages from CSC inspections vs. static CSC guideline values",
    filters: ["1Y", "2Y", "5Y", "All"],
    defaultFilter: "2Y",
    preface: {
      title: "How to read this report",
      text: "CSC Guideline is the static baseline. Observed Average is what CSC inspections show in this portfolio. Larger divergence signals likely guideline updates or higher overbilling risk.",
    },
    metrics: [
      { label: "Inspections Analyzed",  value: "184", delta: "neutral", note: "All time"               },
      { label: "Residential Projects",  value: "112", delta: "neutral", note: "60.9% of portfolio"     },
      { label: "Commercial Projects",   value: "72",  delta: "neutral", note: "39.1% of portfolio"     },
      { label: "Line Items Flagged",    value: "8",   delta: "warn",    note: "Guideline divergence >3%" },
    ],
    rows: [
      [
        {
          title: "Residential - Guideline vs Observed",
          subtitle: "Based on 112 residential inspections",
          wide: true,
          actions: [{ label: "Export CSV", toast: "Residential FI data exported" }],
          table: {
            columns: ["Phase / Line Item", "CSC Guideline %", "Observed Avg %", "Variance", "Inspections", "Flag"],
            rows: [
              ["General Conditions / Job Set Up",    "1.0%", "1.3%", "+0.3%", "112", ""  ],
              ["Excavation / Backfill / Grading",    "6.0%", "7.8%", "+1.8%", "112", "!" ],
              ["Foundation Concrete Poured",         "5.0%", "5.2%", "+0.2%", "112", ""  ],
              ["House Framing / Sheathing",          "9.0%", "9.1%", "+0.1%", "112", ""  ],
              ["Roof Trim / Flashing / Shingling",   "3.0%", "3.5%", "+0.5%", "112", ""  ],
              ["Well / Water Connection",            "2.0%", "3.4%", "+1.4%", "89",  "!" ],
              ["Septic System / Sewer Service",      "3.0%", "4.2%", "+1.2%", "104", "!" ],
              ["Cabinets / Vanities / Counter Tops", "6.0%", "6.8%", "+0.8%", "112", ""  ],
              ["Loam / Landscaping / Paving",        "2.0%", "2.7%", "+0.7%", "112", ""  ],
              ["TOTAL",                              "100%", "100%", "-",     "112", ""  ],
            ],
          },
          insight: {
            tone: "warning",
            title: "Recommended Guideline Updates",
            text: "Excavation, Well/Water, and Septic consistently run above guideline in residential builds.",
          },
          exports: [
            { label: "Copy Table",   toast: "Table copied"   },
            { label: "Export CSV",   toast: "CSV exported"   },
            { label: "Download PDF", toast: "PDF downloaded" },
          ],
        },
      ],
      [
        {
          title: "Commercial - Guideline vs Observed",
          subtitle: "Based on 72 commercial inspections",
          wide: true,
          actions: [{ label: "Export CSV", toast: "Commercial FI data exported" }],
          table: {
            columns: ["CSI Division / Line Item", "CSC Guideline %", "Observed Avg %", "Variance", "Inspections", "Flag"],
            rows: [
              ["General Conditions",          "3.0%",  "3.4%",  "+0.4%", "72", ""  ],
              ["Earthwork / Site Utilities",  "15.0%", "18.2%", "+3.2%", "72", "!" ],
              ["Paving & Curbs",              "7.0%",  "8.1%",  "+1.1%", "72", "!" ],
              ["Concrete",                    "6.5%",  "6.6%",  "+0.1%", "72", ""  ],
              ["Rough Carpentry / Framing",   "9.0%",  "9.2%",  "+0.2%", "72", ""  ],
              ["Roofing",                     "5.0%",  "5.1%",  "+0.1%", "72", ""  ],
              ["Plumbing",                    "3.5%",  "3.6%",  "+0.1%", "72", ""  ],
              ["Electrical",                  "8.0%",  "7.5%",  "-0.5%", "72", ""  ],
              ["Site Management / OHP",       "11.4%", "10.1%", "-1.3%", "72", ""  ],
              ["TOTAL",                       "100%",  "100%",  "-",     "72", ""  ],
            ],
          },
          insight: {
            tone: "warning",
            title: "Commercial Anomaly",
            text: "Earthwork and paving are the largest commercial divergences and should inform lender contingencies.",
          },
        },
      ],
    ],
  },
  {
    id: "costBenchmark",
    navLabel: "Cost Benchmarking",
    navIcon: <CostBenchIcon />,
    navGroup: "Intelligence",
    title: "Regional Cost Benchmarking",
    subtitle: "Cost-per-SF trends, contingency utilization, and change order frequency",
    filters: ["1Y", "2Y", "5Y"],
    defaultFilter: "2Y",
    metrics: [
      { label: "Avg Residential $/SF", value: "$187", delta: "up",      note: "$14 vs prior period"       },
      { label: "Avg Commercial $/SF",  value: "$264", delta: "up",      note: "$22 vs prior period"       },
      { label: "Avg Contingency Used", value: "58%",  delta: "neutral", note: "of budget contingency"     },
      { label: "Change Order Rate",    value: "34%",  delta: "warn",    note: "projects had COs"          },
    ],
    rows: [
      [
        {
          title: "Cost Per SF Trend",
          subtitle: "Residential vs commercial",
          wide: true,
          chart: {
            kind: "line",
            dataKey: "costPerSfTrend",
            lines: [
              { dataKey: "residential", name: "Residential", color: chartPalette.blue },
              { dataKey: "commercial",  name: "Commercial",  color: chartPalette.teal },
            ],
          },
          insight: {
            tone: "info",
            title: "Regional Context",
            text: "Maine construction costs have risen faster than national averages in the last two years.",
          },
        },
      ],
      [
        {
          title: "Contingency Utilization",
          subtitle: "% of contingency drawn",
          chart: {
            kind: "bar",
            dataKey: "contingencyUtilization",
            layout: "vertical",
            bars: [{ dataKey: "value", name: "Utilization %", color: chartPalette.teal }],
          },
        },
        {
          title: "Major Trade Cost Ranges",
          subtitle: "Maine market low / avg / high per SF",
          table: {
            columns: ["Trade", "Low $/SF", "Avg $/SF", "High $/SF"],
            rows: [
              ["Concrete / Foundation", "$10", "$16", "$32"  ],
              ["Framing / Carpentry",   "$18", "$28", "$48"  ],
              ["Roofing",               "$8",  "$14", "$26"  ],
              ["Mechanical (HVAC)",     "$22", "$38", "$110" ],
              ["Electrical",            "$12", "$22", "$55"  ],
              ["Plumbing",              "$10", "$18", "$42"  ],
              ["Sitework",              "$8",  "$22", "$60+" ],
            ],
          },
        },
      ],
    ],
  },
  {
    id: "contractorPerformance",
    navLabel: "Contractor Performance",
    navIcon: <ContractorPerformanceIcon />,
    navGroup: "Intelligence",
    title: "Contractor Performance",
    subtitle: "Overbilling rate, draw accuracy, and pre-con compliance by general contractor",
    metrics: [
      { label: "Active GCs on Platform", value: "11",    delta: "neutral", note: "Across all projects"   },
      { label: "Avg Draw Accuracy",      value: "91.4%", delta: "up",      note: "2.1% YoY"             },
      { label: "Avg Overbilling Rate",   value: "8.6%",  delta: "down",    note: "of draws flagged"      },
      { label: "Pre-Con Completion",     value: "94%",   delta: "up",      note: "on-time submission"    },
    ],
    rows: [
      [
        {
          title: "GC Draw Accuracy Score",
          subtitle: "% matching CSC estimate within +/-5%",
          wide: true,
          chart: {
            kind: "bar",
            dataKey: "gcDrawAccuracy",
            layout: "vertical",
            bars: [{ dataKey: "value", name: "Accuracy %", color: chartPalette.green }],
          },
          insight: {
            tone: "warning",
            title: "Disclosure Note",
            text: "Scores reflect CSC observed accuracy and are not external contractor certifications.",
          },
        },
      ],
      [
        {
          title: "Most Common Overbilled Items",
          subtitle: "Line items flagged most frequently",
          table: {
            columns: ["Line Item", "Flag Count", "Avg Overstatement"],
            rows: [
              ["Electrical Rough-In",         "24", "+18.4%"         ],
              ["Site Management / OHP",       "19", "+12.1%"         ],
              ["Sitework / Excavation",        "18", "+9.6%"          ],
              ["Decks / Exterior Stairs",     "14", "+22.0%"         ],
              ["Siding (materials on site)",   "12", "+31.5%"         ],
              ["Garage Doors (deposit)",       "11", "Deposit policy" ],
            ],
          },
        },
        {
          title: "Pre-Con Checklist Submission Times",
          subtitle: "Days from bank setup to GC submission",
          chart: {
            kind: "bar",
            dataKey: "preconSubmissionTimes",
            layout: "vertical",
            bars: [{ dataKey: "value", name: "Projects", color: chartPalette.blueLight }],
          },
          insight: {
            tone: "success",
            title: "Platform Impact",
            text: "Digital pre-con checklist reduced average submission time from 18.4 days to 6.2 days.",
          },
        },
      ],
    ],
  },
  {
    id: "lienWaiver",
    navLabel: "Lien Waiver Compliance",
    navIcon: <LienWaiverIcon />,
    navGroup: "Operations",
    title: "Lien Waiver Compliance",
    subtitle: "Digital waiver adoption, collection speed, and outstanding balances",
    metrics: [
      { label: "Waivers Sent (Platform)", value: "312",  delta: "up",   note: "84% vs paper baseline"  },
      { label: "Avg Time to Signature",   value: "1.4d", delta: "up",   note: "from 6.2d paper"        },
      { label: "Outstanding Waivers",     value: "27",   delta: "warn", note: "Across 6 projects"      },
      { label: "Digital Adoption Rate",   value: "78%",  delta: "up",   note: "23 pts in 6 months"     },
    ],
    rows: [
      [
        {
          title: "Waivers Collected Over Time",
          subtitle: "Digital vs paper/manual",
          chart: {
            kind: "area",
            dataKey: "waiversCollected",
            lines: [
              { dataKey: "value", name: "Waivers", color: chartPalette.green, fill: "#eaf6ee" },
            ],
          },
        },
        {
          title: "Time to Signature Distribution",
          subtitle: "Days from send to signed",
          chart: {
            kind: "bar",
            dataKey: "signatureDistribution",
            layout: "vertical",
            bars: [{ dataKey: "value", name: "Waivers", color: chartPalette.amber }],
          },
          insight: {
            tone: "success",
            title: "68% signed within 24 hours",
            text: "No-login pre-filled waiver links reduce signature friction dramatically.",
          },
        },
      ],
    ],
  },
  {
    id: "platformUsage",
    navLabel: "Platform Usage",
    navIcon: <PlatformUsageIcon />,
    navGroup: "Operations",
    title: "Platform Usage & Scaling",
    subtitle: "User growth, feature adoption, and CSC platform health over time",
    filters: ["3M", "1Y", "All"],
    defaultFilter: "1Y",
    metrics: [
      { label: "Total Users",             value: "64",  delta: "up", note: "18 in 12 months"  },
      { label: "Active Projects",         value: "29",  delta: "up", note: "8 this quarter"    },
      { label: "Bank Partners",           value: "9",   delta: "up", note: "2 new this year"   },
      { label: "Mobile FI Submissions",   value: "127", delta: "up", note: "100% since launch" },
    ],
    rows: [
      [
        {
          title: "User Growth by Role",
          subtitle: "Cumulative users over time",
          wide: true,
          chart: {
            kind: "line",
            dataKey: "userGrowthByRole",
            lines: [
              { dataKey: "gcOwner",   name: "GC / Owner", color: chartPalette.teal   },
              { dataKey: "lender",    name: "Lender",     color: chartPalette.amber  },
              { dataKey: "cscTeam",   name: "CSC Team",   color: chartPalette.blue   },
              { dataKey: "architect", name: "Architect",  color: chartPalette.purple },
            ],
          },
        },
      ],
      [
        {
          title: "Users by Role",
          chart: {
            kind: "pie",
            dataKey: "usersByRole",
            colors: [
              chartPalette.teal,
              chartPalette.green,
              chartPalette.amber,
              chartPalette.blue,
              chartPalette.purple,
            ],
          },
        },
        {
          title: "Feature Adoption",
          subtitle: "% of eligible projects",
          chart: {
            kind: "bar",
            dataKey: "featureAdoption",
            layout: "vertical",
            bars: [{ dataKey: "value", name: "Adoption %", color: chartPalette.blue }],
          },
        },
        {
          title: "Bank Partner Growth",
          subtitle: "Active lending institutions",
          chart: {
            kind: "line",
            dataKey: "bankPartnerGrowth",
            lines: [{ dataKey: "value", name: "Banks", color: chartPalette.blueLight }],
          },
        },
      ],
    ],
  },
];
